import React from 'react';
import { curr } from 'helpers/curr';
import { DashboardCurrency } from '../dashboardFilters.machine';
import { HeroIconTrendingDown, HeroIconTrendingUp } from 'toolbox';

interface Props {
  totalPosition: number;
  totalPositionChange: number;
  totalPositionChangePct: number;
  annualYield: number;
  averageRate: number;
  changeRate: number;
  changeYield: number;
  latestBalanceDate: string;
  isShowingModelBalances?: boolean;
  currency: DashboardCurrency;
}

const SummaryBar = ({
  totalPosition = 0,
  totalPositionChange = 0,
  totalPositionChangePct = 0,
  annualYield = 0,
  averageRate = 0,
  changeRate = 0,
  changeYield = 0,
  latestBalanceDate = '',
  isShowingModelBalances = false,
  currency,
}: Props) => {
  const interestPointsTrend =
    totalPositionChangePct === 0
      ? 'flat'
      : totalPositionChangePct > 0
      ? 'up'
      : 'down';

  const yieldTrend = interestPointsTrend;

  const balanceChangePct = Number(totalPositionChangePct)
    .toFixed(4)
    .replace(/(\.[1-9]+)([0]+)$/, '$1');

  const yieldChangePct = Number(changeRate)
    .toFixed(4)
    .replace(/(\.[1-9]+)([0]+)$/, '$1');

  return (
    <>
      <div className="flex flex-col justify-center flex-grow px-5 py-3 text-gray-100 rounded-lg md:flex-row bg-jcap-900">
        <div className="flex justify-center overflow-x-hidden">
          {/* TOTAL POSITION AS GBP */}
          <div className="flex flex-shrink-0">
            <div className="flex flex-col overflow-x-hidden">
              <div className="text-sm font-semibold tracking-wide uppercase text-jcap-300">
                Total Position as {currency}
              </div>
              <div className="flex items-center">
                <div className="text-3xl text-gray-200">
                  {curr(totalPosition, currency)}
                </div>
              </div>
            </div>
            <div className="flex flex-col ml-5 overflow-x-hidden text-base text-right opacity-75 xl:ml-10">
              <div className="text-sm font-semibold leading-tight tracking-wide uppercase text-jcap-300 ">
                {isShowingModelBalances ? 'Model vs. Actual' : 'Latest Change'}
              </div>
              <div className="text-base font-semibold leading-tight uppercase">
                {interestPointsTrend === 'up' ? (
                  <>
                    <HeroIconTrendingUp className="inline h-4 -mt-1 -ml-1 text-green-500" />
                    {balanceChangePct}%
                  </>
                ) : interestPointsTrend === 'down' ? (
                  <>
                    <HeroIconTrendingDown className="inline h-4 -mt-px -ml-1 text-red-500" />
                    {balanceChangePct}%
                  </>
                ) : (
                  <>{balanceChangePct}%</>
                )}
              </div>
              <div className="text-base font-semibold leading-tight uppercase">
                {yieldTrend === 'up' ? (
                  <>
                    <HeroIconTrendingUp className="inline h-4 -mt-1 -ml-1 text-green-500" />
                    {curr(totalPositionChange, currency)}
                  </>
                ) : yieldTrend === 'down' ? (
                  <>
                    <HeroIconTrendingDown className="inline h-4 -mt-px -ml-1 text-red-500" />
                    {curr(totalPositionChange).replace('-', '')}
                  </>
                ) : (
                  <>{curr(totalPositionChange, currency)}</>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex-shrink mx-6 my-1 border-t opacity-50 md:border-t-none md:border-l border-jcap-400 xl:mx-16" />
        <div className="flex justify-center overflow-x-hidden">
          {/* ANNUAL YIELD */}
          <div className="flex flex-shrink-0">
            <div className="flex flex-col overflow-x-hidden">
              <div className="text-sm font-semibold tracking-wide uppercase text-jcap-300">
                Annual Yield
              </div>
              <div className="flex items-center">
                <div className="text-3xl text-gray-200">
                  {curr(annualYield, currency)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-shrink mx-2 xl:mx-4" />

          {/* WEIGHTED AER */}
          <div className="flex flex-shrink-0">
            <div className="flex flex-col overflow-x-hidden">
              <div className="text-sm font-semibold tracking-wide uppercase text-jcap-300">
                Average Rate
              </div>
              <div className="flex items-center">
                <div className="text-3xl text-gray-200">
                  {(Number(averageRate)
                    .toFixed(4)
                    .replace(/^0.0000$/, '') || 0) + '%'}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-shrink mx-2 xl:mx-4" />

          {/* BALANCE / MODEL CHANGE */}

          <div className="flex flex-shrink-0">
            {' '}
            <div className="flex flex-col ml-5 overflow-x-hidden text-base text-right opacity-75 xl:ml-10">
              <div className="text-sm font-semibold leading-tight tracking-wide uppercase text-jcap-300 ">
                {isShowingModelBalances ? 'MODEL VS. ACTUAL' : 'LATEST CHANGE'}
              </div>
              <div className="text-base font-semibold leading-tight uppercase">
                <span className="text-sm text-jcap-300">RATE</span>{' '}
                {Number(yieldChangePct) > 0 ? (
                  <>
                    <HeroIconTrendingUp className="inline h-4 -mt-1 -ml-1 text-green-500" />
                    {Number(yieldChangePct).toFixed(4)}
                  </>
                ) : Number(yieldChangePct) < 0 ? (
                  <>
                    <HeroIconTrendingDown className="inline h-4 -mt-px -ml-1 text-red-500" />
                    {Number(yieldChangePct).toFixed(4)}
                  </>
                ) : (
                  <>{Number(yieldChangePct).toFixed(4)}</>
                )}
                %
              </div>
              <div className="text-base font-semibold leading-tight uppercase">
                {Number(changeYield) > 0 ? (
                  <>
                    <span className="text-sm text-jcap-300">YIELD</span>{' '}
                    <HeroIconTrendingUp className="inline h-4 -mt-1 -ml-1 text-green-500" />
                    {curr(changeYield, currency)}
                  </>
                ) : Number(changeYield) < 0 ? (
                  <>
                    <span className="text-sm text-jcap-300">YIELD</span>{' '}
                    <HeroIconTrendingDown className="inline h-4 -mt-px -ml-1 text-red-500" />
                    {curr(changeYield, currency).replace('-', '')}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-jcap-300">YIELD</span>{' '}
                    {curr(changeYield, currency).replace('-', '')}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-start mt-2 text-sm text-gray-600 cursor-default select-none">
        <p>
          Latest balance and exchange rate information dated{' '}
          {new Date(latestBalanceDate).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          . FX valuations used in a Model may be more recent than the exchange
          rate used to show your latest balance.
        </p>
      </div>
    </>
  );
};

export default SummaryBar;
